import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ClientPortalLayout from "./ClientPortalLayout";
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  Construction as ConstructionIcon,
  Schedule as ScheduleIcon,
  AttachFile as FileIcon,
  Payment as PaymentIcon,
  TrendingUp as ProgressIcon,
  Build as BuildIcon,
} from "@mui/icons-material";
import { useSnackbar } from "@codejq/react-simple-snackbar";
import { useDispatch } from "react-redux";
import { getConstructionPortalData } from "../../actions/clientPortalActions";
import { format } from "date-fns";

const ConstructionClientPortal = () => {
  const history = useHistory();
  const { t, i18n } = useTranslation("crm");
  const [openSnackbar] = useSnackbar();
  const dispatch = useDispatch();
  
  // Helper function to normalize language code (e.g., en-US -> en)
  const normalizeLanguage = (lang) => {
    if (!lang) return 'en';
    return lang.split('-')[0];
  };
  
  const currentLang = normalizeLanguage(i18n.language);

  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [invoices, setInvoices] = useState([]);

  // Fetch construction portal data using Redux
  const fetchConstructionData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await dispatch(getConstructionPortalData());
      setProjects(data.projects || []);
      setTasks(data.tasks || []);
      setDocuments(data.documents || []);
      setInvoices(data.invoices || []);
    } catch (error) {
      console.error("Error fetching construction data:", error);
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchConstructionData();
  }, [fetchConstructionData]);

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'completed':
      case 'paid':
        return 'success';
      case 'in progress':
      case 'active':
        return 'primary';
      case 'pending':
        return 'warning';
      case 'delayed':
      case 'overdue':
        return 'error';
      default:
        return 'default';
    }
  };

  const modules = [
    {
      name: 'construction',
      label: 'Construction Projects',
      icon: <ConstructionIcon />,
      path: '/client-portal/construction'
    }
  ];

  if (loading) {
    return (
      <ClientPortalLayout modules={modules} currentModule="construction">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress size={60} />
        </Box>
      </ClientPortalLayout>
    );
  }

  return (
    <ClientPortalLayout modules={modules} currentModule="construction">
      <Box>
        <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
          <ConstructionIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          {t('construction_portal')}
        </Typography>

        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
          <Tab label={t('overview')} icon={<BuildIcon />} iconPosition="start" />
          <Tab label={t('projects')} icon={<ConstructionIcon />} iconPosition="start" />
          <Tab label={t('tasks')} icon={<ScheduleIcon />} iconPosition="start" />
          <Tab label={t('documents')} icon={<FileIcon />} iconPosition="start" />
          <Tab label={t('billing')} icon={<PaymentIcon />} iconPosition="start" />
        </Tabs>

        {/* Overview Tab */}
        {activeTab === 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>{t('active_projects')}</Typography>
                  <Typography variant="h4">{projects.length}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>{t('ongoing_tasks')}</Typography>
                  <Typography variant="h4">{tasks.filter(t => t.status === 'In Progress').length}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>{t('documents')}</Typography>
                  <Typography variant="h4">{documents.length}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>{t('pending_payments')}</Typography>
                  <Typography variant="h4">{invoices.filter(i => i.status === 'Pending').length}</Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Recent Projects */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>{t('recent_projects')}</Typography>
                <List>
                  {projects.slice(0, 3).map((project) => (
                    <React.Fragment key={project._id}>
                      <ListItem>
                        <ListItemText
                          primary={project.projectName}
                          secondary={`${project.projectNumber} • ${project.progress}% Complete`}
                        />
                        <Chip label={project.status} color={getStatusColor(project.status)} size="small" />
                      </ListItem>
                      <Divider />
                    </React.Fragment>
                  ))}
                </List>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Projects Tab */}
        {activeTab === 1 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>{t('projects')}</Typography>
            {projects.length === 0 ? (
              <Alert severity="info">{t('no_projects_found')}</Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('project_name')}</TableCell>
                      <TableCell>{t('project_number')}</TableCell>
                      <TableCell>{t('status')}</TableCell>
                      <TableCell>{t('progress')}</TableCell>
                      <TableCell>{t('estimated_completion')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {projects.map((project) => (
                      <TableRow key={project._id}>
                        <TableCell>{project.projectName}</TableCell>
                        <TableCell>{project.projectNumber}</TableCell>
                        <TableCell>
                          <Chip label={project.status} color={getStatusColor(project.status)} size="small" />
                        </TableCell>
                        <TableCell>{project.progress}%</TableCell>
                        <TableCell>{format(new Date(project.estimatedCompletion), 'MMM dd, yyyy')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        )}

        {/* Tasks Tab */}
        {activeTab === 2 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>{t('tasks')}</Typography>
            {tasks.length === 0 ? (
              <Alert severity="info">{t('no_tasks_assigned')}</Alert>
            ) : (
              <List>
                {tasks.map((task) => (
                  <React.Fragment key={task._id}>
                    <ListItem>
                      <ListItemText
                        primary={task.taskName}
                        secondary={`Due: ${format(new Date(task.dueDate), 'MMM dd, yyyy')} • Assigned to: ${task.assignedTo}`}
                      />
                      <Chip label={task.status} color={getStatusColor(task.status)} size="small" />
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        )}

        {/* Documents Tab */}
        {activeTab === 3 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>{t('documents')}</Typography>
            {documents.length === 0 ? (
              <Alert severity="info">{t('no_documents_available')}</Alert>
            ) : (
              <List>
                {documents.map((doc) => (
                  <React.Fragment key={doc._id}>
                    <ListItem button>
                      <FileIcon sx={{ mr: 2 }} />
                      <ListItemText
                        primary={doc.title}
                        secondary={`${doc.fileName} • Uploaded: ${format(new Date(doc.uploadedDate), 'MMM dd, yyyy')}`}
                      />
                      <Chip label={doc.category} size="small" />
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        )}

        {/* Billing Tab */}
        {activeTab === 4 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>{t('invoices')}</Typography>
            {invoices.length === 0 ? (
              <Alert severity="info">{t('no_invoices_found')}</Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('invoice_number')}</TableCell>
                      <TableCell>{t('amount')}</TableCell>
                      <TableCell>{t('status')}</TableCell>
                      <TableCell>{t('due_date')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoices.map((invoice) => (
                      <TableRow key={invoice._id}>
                        <TableCell>{invoice.invoiceNumber}</TableCell>
                        <TableCell>${invoice.amount.toLocaleString()}</TableCell>
                        <TableCell>
                          <Chip label={invoice.status} color={getStatusColor(invoice.status)} size="small" />
                        </TableCell>
                        <TableCell>{format(new Date(invoice.dueDate), 'MMM dd, yyyy')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        )}
      </Box>
    </ClientPortalLayout>
  );
};

export default ConstructionClientPortal;
